import { css } from 'lit';
import { hostReset, floatingPanel, placementOrigins } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  // Panel enters/exits via the floatingPanel mixin — same as dropdown/popover/tooltip
  floatingPanel('.uwc-cp__panel', { durationVar: '--uwc-cp-duration', durationDefault: '140ms' }),
  placementOrigins,
  css`
    :host {
      display: inline-block;
      vertical-align: middle;
    }

    /* ── Trigger swatch ─────────────────────────────────────────────────────── */
    .uwc-cp__swatch {
      --_size:   var(--uwc-colorpicker-swatch-size,   2rem);
      --_radius: var(--uwc-colorpicker-swatch-radius, ${t.radiusMd});

      display: inline-flex;
      align-items: center;
      justify-content: center;
      width:         var(--_size);
      height:        var(--_size);
      border-radius: var(--_radius);
      border:        1px solid rgba(0,0,0,0.15);
      background:    var(--_swatch-bg, #ffffff);
      cursor:        pointer;
      transition:    box-shadow ${t.durationBase};
      flex-shrink:   0;
    }
    .uwc-cp__swatch:hover {
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 30%, transparent);
    }
    .uwc-cp__swatch:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
    }
    .uwc-cp__swatch--open {
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 30%, transparent);
    }

    /* Disabled */
    :host([disabled]) { pointer-events: none; opacity: 0.6; }

    /* ── Panel (popover) ────────────────────────────────────────────────────── */
    /* floatingPanel mixin sets position:fixed + opacity/transform animation but
       does NOT set pointer-events. We must do it here so the panel never blocks
       clicks while it is animating out (overlay allow-discrete keeps it in the
       top layer for the full duration of the exit transition). */
    .uwc-cp__panel {
      pointer-events: none;   /* ← closed: invisible AND non-interactive */
      width:         var(--uwc-colorpicker-panel-width, 13.5rem);
      background:    var(--uwc-cp-panel-bg, ${t.surface});
      border:        1px solid rgba(0,0,0,0.10);
      border-radius: ${t.radiusMd};
      box-shadow:    ${t.shadowLg};
      padding:       ${t.space3};
      display:       flex;
      flex-direction: column;
      gap:           0.625rem;
      /* Reset browser popover styles */
      color:         ${t.text};
      font-family:   inherit;
      font-size:     ${t.fontSizeMd};
    }
    /* Only restore pointer-events when the panel is actually open */
    .uwc-cp__panel.is-open { pointer-events: auto; }

    /* ── Inline mode ────────────────────────────────────────────────────────── */
    :host([inline]) .uwc-cp__swatch  { display: none; }
    :host([inline]) .uwc-cp__panel {
      /* Override floatingPanel: always visible, static flow */
      position:  static !important;
      opacity:   1 !important;
      transform: none !important;
      pointer-events: auto !important;
      box-shadow: ${t.shadowSm};
    }

    /* ── Color area (SV gradient box) ───────────────────────────────────────── */
    .uwc-cp__area {
      position:    relative;
      width:       100%;
      aspect-ratio: 1 / 0.72;
      border-radius: ${t.radiusSm};
      overflow:    hidden;
      cursor:      crosshair;
      touch-action: none;
      user-select: none;
    }
    /* Hue layer — right gradient from white to pure hue */
    .uwc-cp__area-hue {
      position: absolute;
      inset: 0;
      background: linear-gradient(to right, #fff, hsl(var(--_hue, 0), 100%, 50%));
    }
    /* Dark layer — top gradient from transparent to black */
    .uwc-cp__area-dark {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, #000, transparent);
    }
    /* Cursor circle */
    .uwc-cp__cursor {
      position:      absolute;
      width:         12px;
      height:        12px;
      border-radius: 50%;
      border:        2px solid #fff;
      box-shadow:    0 0 0 1px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(0,0,0,0.1);
      transform:     translate(-50%, -50%);
      pointer-events: none;
    }

    /* ── Hue slider ─────────────────────────────────────────────────────────── */
    .uwc-cp__hue-track {
      position:      relative;
      height:        10px;
      border-radius: ${t.radiusFull};
      background:    linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);
      cursor:        pointer;
      touch-action:  none;
      user-select:   none;
    }

    /* ── Alpha slider ───────────────────────────────────────────────────────── */
    .uwc-cp__alpha-track {
      position:      relative;
      height:        10px;
      border-radius: ${t.radiusFull};
      cursor:        pointer;
      touch-action:  none;
      user-select:   none;
      background-image:
        linear-gradient(to right, transparent, var(--_alpha-color, #6366f1)),
        repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0 / 10px 10px;
    }

    /* Shared slider thumb */
    .uwc-cp__thumb {
      position:      absolute;
      top:           50%;
      width:         14px;
      height:        14px;
      border-radius: 50%;
      background:    #fff;
      box-shadow:    0 0 0 1px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.2);
      transform:     translate(-50%, -50%);
      pointer-events: none;
    }

    /* ── Preview row ────────────────────────────────────────────────────────── */
    .uwc-cp__preview-row {
      display:     flex;
      align-items: center;
      gap:         ${t.space2};
    }
    .uwc-cp__preview-swatch {
      width:         1.75rem;
      height:        1.75rem;
      border-radius: ${t.radiusSm};
      border:        1px solid rgba(0,0,0,0.12);
      flex-shrink:   0;
    }
    .uwc-cp__sliders {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    /* ── Format inputs ──────────────────────────────────────────────────────── */
    /* align-items:flex-end → button bottom aligns with input bottom,
       format-label bottom aligns with input-label bottom. */
    .uwc-cp__inputs {
      display:     flex;
      gap:         0.375rem;
      align-items: flex-end;
    }

    /* Wrap the format button in the same column-flex structure as an input-wrap
       so it has an equal "label row" at the bottom — keeps all columns bottom-aligned */
    .uwc-cp__format-col {
      display:        flex;
      flex-direction: column;
      align-items:    center;
      gap:            0.125rem;
      flex-shrink:    0;
    }
    .uwc-cp__format-btn {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      width:           2rem;
      height:          1.5rem;
      border:          1px solid #d1d5db;
      border-radius:   ${t.radiusSm};
      background:      transparent;
      cursor:          pointer;
      color:           ${t.textSecondary};
      font-size:       0.6rem;
      font-weight:     ${t.fontWeightBold};
      letter-spacing:  0.03em;
      transition:      background ${t.durationBase};
    }
    .uwc-cp__format-btn:hover { background: ${t.hoverBg}; }

    /* Invisible spacer that matches the height of .uwc-cp__input-label so
       the format button column is the same total height as an input-wrap column */
    .uwc-cp__format-spacer {
      display:    block;
      height:     0.75rem;   /* matches label line-height */
      visibility: hidden;
    }

    .uwc-cp__input-group {
      display: flex;
      flex:    1;
      gap:     0.25rem;
    }
    .uwc-cp__input-wrap {
      display:         flex;
      flex-direction:  column;
      align-items:     center;
      flex:            1;
      gap:             0.125rem;
    }
    .uwc-cp__input-field {
      width:       100%;
      padding:     0.25rem;
      font-size:   0.7rem;
      font-family: inherit;
      text-align:  center;
      border:      1px solid #d1d5db;
      border-radius: ${t.radiusXs};
      outline:     none;
      background:  ${t.surface};
      color:       ${t.text};
      transition:  border-color ${t.durationBase};
    }
    .uwc-cp__input-field:focus { border-color: var(--uwc-color-primary, ${t.primary}); }
    .uwc-cp__input-label {
      font-size:      0.6rem;
      line-height:    0.75rem;
      color:          ${t.textMuted};
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  `,
];
